// Instanced renderer for the Severed Array (ENEMY-REFORGE). One body + glow (+ outline) InstancedMesh per family variant
// (normal / elite) and one per boss; per-instance vec4 drives the GPU gait (see kit.ts). Reads Sim state only.
import * as THREE from 'three/webgpu';
import type { EnemyKind } from '../../core/content';
import { animatedOutlineGeometry, animatedOutlineMaterial, animatedPosition, ArtBuilder, EnemyArt } from './kit';
import { BOSS_ART, FAMILY_ART } from './index';
import { RenderStyle, toonMaterial } from '../toon';
import { deathNodes } from './dissolve';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { color, float, mix, normalView, positionViewDirection } from 'three/tsl';
import type { EnemySkin } from '../mapkit';
import { NIGHT } from '../timeOfDay';
import { ANIME, ANIME_INK, animeInk, animeRim } from '../animeLook';
import { formArt } from '../bossFormLooks'; // BOSS FORMS (owner 2026-11-02)
import { isMapFoe, MAP_FOE_KINDS, mapFoeKindsFor } from '../../core/mapFoeDefs'; // MAP FOES (owner 2026-11-02)

/** MAP FOES: instance slots per map-only foe batch (roster caps are 2–14 alive, plus dying bodies) */
const MAP_FOE_BATCH = 48;

interface Batch {
  art: EnemyArt; body: THREE.InstancedMesh; glow: THREE.InstancedMesh; outline: THREE.InstancedMesh | null;
  anim: THREE.InstancedBufferAttribute; anim2: THREE.InstancedBufferAttribute; anim3: THREE.InstancedBufferAttribute | null; anim4: THREE.InstancedBufferAttribute | null;
  n: number; cap: number; height: number;
  /** ENEMY-DEATHS: per body-part group (kit.ts PART) centroid x, y, z and extent radius in model space (radius 0 = group absent) */
  parts: Float32Array | null;
}
const _c = new THREE.Color();

export class EnemyArtRenderer {
  private batches = new Map<string, Batch>();
  /** ENEMY-DEATHS: death shading (dissolve / hole / cut / blown-apart parts) is compiled in (only with ?deaths=new / ?deathlab) */
  readonly deaths: boolean;
  /** MAP SYSTEM: skin = the map's enemy reskin (colour swaps / tint / extra parts), bossArt = the map's final boss look
   *  (drawn for the 'HOLLOW TYRANT' key, which view.ts uses for whichever final boss the map has) */
  /** BOSS FORMS: finalName = the map's final boss (its forms are built from bossArt); every boss gets two more batches,
   *  'boss:<key>#1' and 'boss:<key>#2', for its second and third forms (src/render/bossFormLooks.ts) */
  /** MAP FOES: mapId = the map being drawn; only its own map-only foes get batches (never elites, no map skin on top);
   *  '*' = all of them (animLab). PERF PASS 3: deferBuild = the per-family steps run later (build() / buildChunked()). */
  constructor(private scene: THREE.Scene, private style: RenderStyle, private shadows: boolean, cap: number, deaths = false, private skin: EnemySkin | null = null, bossArt: EnemyArt | null = null, finalName = 'HOLLOW TYRANT', mapId = '', deferBuild = false) {
    this.deaths = deaths;
    this.steps = [];
    const own = (mapId === '*' ? MAP_FOE_KINDS : mapFoeKindsFor(mapId)) as readonly string[]; // ('*' = every map's: the labs)
    for (const [kind, art] of Object.entries(FAMILY_ART)) {
      if (isMapFoe(kind)) { if (own.includes(kind)) this.steps.push(() => this.make(`${kind}:n`, art, false, Math.min(cap, MAP_FOE_BATCH), kind as EnemyKind, false)); continue; } // MAP FOES: one chunked step per own map foe
      this.steps.push(() => { this.make(`${kind}:e`, art, true, Math.min(cap, 80), kind as EnemyKind); this.make(`${kind}:n`, art, false, cap, kind as EnemyKind); });
    }
    for (const [name, art] of Object.entries(BOSS_ART)) {
      const a = name === 'HOLLOW TYRANT' && bossArt ? bossArt : art, real = name === 'HOLLOW TYRANT' ? finalName : name;
      this.steps.push(() => { this.make(`boss:${name}`, a, false, 2, null); for (const f of [1, 2]) this.make(`boss:${name}#${f}`, formArt(a, real, f), false, 1, null); }); // BOSS FORMS
    }
    if (!deferBuild) this.build();
  }
  /** the per-family / per-boss build steps still to run (PERF PASS 3: the Run Setup pre-build runs them chunked) */
  private steps: (() => void)[] = [];
  private runStep(fn: () => void): void { ArtBuilder.partIds = this.deaths; try { fn(); } finally { ArtBuilder.partIds = false; } }
  /** build everything now (the constructor does this unless deferBuild) */
  build(): void { for (const fn of this.steps) this.runStep(fn); this.steps = []; }
  /** build one family per macrotask (~30-50 ms each) so a menu stays responsive; safe to call while build() is pending */
  async buildChunked(): Promise<void> {
    while (this.steps.length) { this.runStep(this.steps.shift()!); if (this.steps.length) await new Promise((r) => setTimeout(r, 0)); }
  }
  /** true once every family and boss batch exists */
  get built(): boolean { return this.steps.length === 0; }
  /** MAP SYSTEM: remove every batch from the scene (map change) */
  dispose(): void {
    for (const b of this.batches.values()) for (const m of [b.body, b.glow, b.outline]) if (m) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); m.dispose(); }
    this.batches.clear();
  }
  /** apply the map skin to a family's built geometry: dress parts merged in, colour swaps + tint on body, glow tint */
  private reskin(geo: { body: THREE.BufferGeometry; glow: THREE.BufferGeometry }, kind: EnemyKind | null, elite: boolean): void {
    const sk = this.skin; if (!sk || !kind) return;
    if (sk.dress) {
      const b = new ArtBuilder(); sk.dress(kind, elite, b);
      const extra = (b as unknown as { body: unknown[]; glow: unknown[] }).body.length + (b as unknown as { glow: unknown[] }).glow.length ? b.build() : null;
      if (extra) for (const part of ['body', 'glow'] as const) {
        if (!extra[part].getAttribute('position')?.count) continue;
        const merged = geo[part].getAttribute('position')?.count ? mergeGeometries([geo[part], extra[part]], false) : extra[part];
        if (merged) geo[part] = merged; else console.warn('[map skin] dress parts could not merge for', kind);
      }
    }
    const swap = sk.swap ? new Map(Object.entries(sk.swap).map(([a, b]) => [Number(a), new THREE.Color(b)])) : null;
    const tint = sk.tint, glow = sk.glow;
    const recolor = (g: THREE.BufferGeometry, isGlow: boolean) => {
      const c = g.getAttribute('color') as THREE.BufferAttribute | undefined; if (!c) return;
      for (let i = 0; i < c.count; i++) {
        _c.setRGB(c.getX(i), c.getY(i), c.getZ(i));
        if (swap) { const to = swap.get(_c.getHex()); if (to) _c.copy(to); }
        const t = isGlow ? glow : tint;
        if (t) _c.setRGB(_c.r * t[0], _c.g * t[1], _c.b * t[2]);
        c.setXYZ(i, _c.r, _c.g, _c.b);
      }
      c.needsUpdate = true;
    };
    recolor(geo.body, false); recolor(geo.glow, true);
  }
  private make(key: string, art: EnemyArt, elite: boolean, cap: number, kind: EnemyKind | null, skinned = true): void {
    const geo = art.build(elite);
    if (skinned) this.reskin(geo, kind, elite);
    const anim = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    anim.setUsage(THREE.DynamicDrawUsage);
    const anim2 = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); anim2.setUsage(THREE.DynamicDrawUsage);
    for (const g of [geo.body, geo.glow]) { g.setAttribute('anim', anim); g.setAttribute('anim2', anim2); }
    geo.body.computeBoundingBox(); const height = geo.body.boundingBox ? geo.body.boundingBox.max.y : 2;
    let anim3: THREE.InstancedBufferAttribute | null = null, anim4: THREE.InstancedBufferAttribute | null = null, parts: Float32Array | null = null;
    const dz = this.deaths ? deathNodes() : null;
    if (dz) {
      const mk = () => { const a = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); a.setUsage(THREE.DynamicDrawUsage); return a; };
      anim3 = mk(); anim4 = mk();
      for (const g of [geo.body, geo.glow]) { g.setAttribute('anim3', anim3); g.setAttribute('anim4', anim4); }
      parts = partCentroids(geo.body);
    }
    const pos = animatedPosition(!!dz);
    const bm = this.style === 'cel' ? toonMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: 0.6, metalness: 0.15 });
    bm.vertexColors = true; (bm as THREE.NodeMaterial).positionNode = pos;
    // MAP SYSTEM night readability: a cool rim light on every foe, scaled by NIGHT.rim (0 by day: no change)
    const nightRim = color(0x9cc4ff).mul(float(1).sub(normalView.dot(positionViewDirection).abs()).pow(2.2)).mul(NIGHT.rim.mul(0.85));
    // ?look=anime: hard back-light rim on every foe and boss (separates silhouettes from the ground in busy fights)
    // ?look=anime (P1 hierarchy): bosses get a rim that spikes on states (telegraph / stagger), elites a modest one, the
    // ordinary horde none
    const boss = key.startsWith('boss:');
    const rim = !ANIME.enabled || (!boss && !elite) ? nightRim : nightRim.add(animeRim(boss ? ANIME.bossRim.mul(0.6).add(0.3) : float(0.35)));
    (bm as THREE.MeshStandardNodeMaterial).emissiveNode = rim;
    if (dz) { (bm as THREE.NodeMaterial).maskNode = dz.mask; (bm as THREE.MeshStandardNodeMaterial).emissiveNode = dz.emissive.add(rim); bm.side = THREE.DoubleSide; }
    const body = new THREE.InstancedMesh(geo.body, bm, cap);
    body.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    body.castShadow = this.shadows; body.frustumCulled = false; body.count = 0;
    const gm = new THREE.MeshBasicNodeMaterial({ fog: false, vertexColors: true }); gm.positionNode = pos;
    if (dz) gm.maskNode = dz.mask;
    const glow = new THREE.InstancedMesh(geo.glow, gm, cap);
    glow.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    glow.frustumCulled = false; glow.count = 0;
    this.scene.add(body, glow);
    let outline: THREE.InstancedMesh | null = null;
    if (this.style === 'cel') {
      const og = animatedOutlineGeometry(geo.body); og.setAttribute('anim', anim); og.setAttribute('anim2', anim2);
      // ?look=anime: navy ink; bosses heavier, elites as today, the ordinary horde thinner (outlines never merge into a mass)
      const om = animatedOutlineMaterial(art.outline ?? 0.035, 0x0b0612, !!dz, ANIME.enabled ? animeInk(key.startsWith('boss:') ? 1.5 : elite ? 1 : 0.7) : null);
      const ink0 = ANIME.enabled ? mix(color(0x0b0612), color(ANIME_INK), ANIME.k) : color(0x0b0612);
      om.colorNode = mix(ink0, color(0x7fa6ff), NIGHT.rim.mul(0.75)); // night: the ink line turns into a pale moonlit edge
      if (dz && anim3 && anim4) { og.setAttribute('anim3', anim3); og.setAttribute('anim4', anim4); om.maskNode = dz.mask; om.colorNode = dz.outlineColor(0x0b0612); }
      outline = new THREE.InstancedMesh(og, om, cap);
      outline.instanceMatrix = body.instanceMatrix; outline.frustumCulled = false; outline.count = 0;
      this.scene.add(outline);
    }
    // owner 2026-11-02 (enemies invisible on mobile / WebGPU): a batch fed the GPU 12 vertex buffers (6 per-vertex: position,
    // normal, colour, pivot, limb, part + 6 per-instance: anim…anim4, instance matrix / colour). WebGPU allows 8, so the
    // pipeline silently failed and only the (simpler) shadow pass drew. Pack every per-vertex attribute into ONE interleaved
    // buffer: ≤ 7 buffers, same data, same shaders.
    for (const g of [geo.body, geo.glow, outline?.geometry]) if (g) interleaveVertexAttributes(g);
    this.batches.set(key, { art, body, glow, outline, anim, anim2, anim3, anim4, n: 0, cap, height, parts });
  }

  /** art for a kind/boss (null if unknown) */
  art(kind: EnemyKind, bossName: string | null): EnemyArt | null {
    if (bossName) return BOSS_ART[bossName.split('#')[0]] ?? null; // ('#n' = a boss form's batch)
    return kind === 'boss' ? BOSS_ART['HOLLOW TYRANT'] : FAMILY_ART[kind as Exclude<EnemyKind, 'boss'>] ?? null;
  }
  begin(): void { for (const b of this.batches.values()) b.n = 0; }
  /**
   * Place one enemy. m = world matrix; tint = body multiplier (white normally; hit flash / stun tints);
   * glowK = glow intensity; anim = [gait phase, stride 0..1, wind-up 0..1, hit 0..1].
   */
  push(kind: EnemyKind, elite: boolean, bossName: string | null, m: THREE.Matrix4, tint: THREE.Color, glowK: number, a0: number, a1: number, a2: number, a3: number): boolean {
    const key = bossName ? `boss:${bossName}` : kind === 'boss' ? 'boss:HOLLOW TYRANT' : `${kind}:${elite ? 'e' : 'n'}`;
    const b = this.batches.get(key); if (!b || b.n >= b.cap) return false;
    const i = b.n++;
    b.body.setMatrixAt(i, m); b.glow.setMatrixAt(i, m);
    b.body.setColorAt(i, tint);
    _c.setRGB(glowK, glowK, glowK); b.glow.setColorAt(i, _c);
    const A = b.anim.array as Float32Array; A[i * 4] = a0; A[i * 4 + 1] = a1; A[i * 4 + 2] = a2; A[i * 4 + 3] = a3;
    // instance basis for the GPU limb offsets: yaw and uniform scale extracted from the matrix
    const e = m.elements, sx = Math.hypot(e[0], e[1], e[2]) || 1;
    const B = b.anim2.array as Float32Array; B[i * 4] = e[0] / sx; B[i * 4 + 1] = -e[2] / sx; B[i * 4 + 2] = sx; B[i * 4 + 3] = 0;
    if (b.anim4) (b.anim4.array as Float32Array)[i * 4] = 0; // the slot may have held a dying body last frame
    return true;
  }
  /** model top above the feet (m, unscaled, incl. hover) for a family or a named boss — enemy health bars sit over it */
  topOf(kind: EnemyKind, bossName: string | null): number {
    const key = bossName ? `boss:${bossName}` : kind === 'boss' ? 'boss:HOLLOW TYRANT' : `${kind}:n`;
    return (this.batches.get(key)?.height ?? 2) + (this.art(kind, bossName)?.hover ?? 0);
  }
  /** model height (m, unscaled) of a family's body — death animations size their sink / pivots from it */
  height(kind: EnemyKind): number { return this.batches.get(`${kind}:n`)?.height ?? 2; }
  /** ENEMY-DEATHS: body-part centroids + radii for a family variant (see Batch.parts), null without death shading */
  parts(kind: EnemyKind, elite: boolean): Float32Array | null { return this.batches.get(`${kind}:${elite ? 'e' : 'n'}`)?.parts ?? null; }
  /**
   * ENEMY-DEATHS: place one dying body / body piece in its family batch (after the live foes, so they keep priority for the
   * slots). Rest pose (no gait / wind-up / recoil, wings neutral) so the matrix alone animates it. u = dissolve progress,
   * rgb = energy colour, ang = model-space angle, mode/a/bb/heat = anim4 (see dissolve.ts). False when the batch is full.
   */
  pushDead(kind: EnemyKind, elite: boolean, m: THREE.Matrix4, tint: THREE.Color, glowK: number, u = 0, r = 1, g = 1, bl = 1, ang = 0, mode = 0, a = 0, bb = 0, heat = 0): boolean {
    const b = this.batches.get(`${kind}:${elite ? 'e' : 'n'}`); if (!b || b.n >= b.cap) return false;
    const i = b.n++;
    b.body.setMatrixAt(i, m); b.glow.setMatrixAt(i, m);
    b.body.setColorAt(i, tint);
    _c.setRGB(glowK, glowK, glowK); b.glow.setColorAt(i, _c);
    // phase -pi/4 zeroes the wing roll term (sin(2x) = -1), stride / wind-up / recoil 0: no limb offsets
    const A = b.anim.array as Float32Array; A[i * 4] = -Math.PI / 4; A[i * 4 + 1] = 0; A[i * 4 + 2] = 0; A[i * 4 + 3] = 0;
    const e = m.elements, sx = Math.hypot(e[0], e[1], e[2]) || 1;
    const B = b.anim2.array as Float32Array; B[i * 4] = 1; B[i * 4 + 1] = 0; B[i * 4 + 2] = sx; B[i * 4 + 3] = this.deaths ? u : 0;
    if (b.anim3) { const C = b.anim3.array as Float32Array; C[i * 4] = r; C[i * 4 + 1] = g; C[i * 4 + 2] = bl; C[i * 4 + 3] = ang; }
    if (b.anim4) { const D = b.anim4.array as Float32Array; D[i * 4] = mode; D[i * 4 + 1] = a; D[i * 4 + 2] = bb; D[i * 4 + 3] = heat; }
    return true;
  }
  end(): void {
    for (const b of this.batches.values()) {
      b.body.count = b.n; b.glow.count = b.n; if (b.outline) b.outline.count = b.n;
      if (!b.n) continue;
      b.body.instanceMatrix.needsUpdate = true; b.glow.instanceMatrix.needsUpdate = true;
      b.body.instanceColor!.needsUpdate = true; b.glow.instanceColor!.needsUpdate = true;
      b.anim.needsUpdate = true; b.anim2.needsUpdate = true; if (b.anim3) b.anim3.needsUpdate = true; if (b.anim4) b.anim4.needsUpdate = true;
    }
  }
  setVisible(v: boolean): void { for (const b of this.batches.values()) { b.body.visible = v; b.glow.visible = v; if (b.outline) b.outline.visible = v; } }
}

/** centroid (x, y, z) and extent radius of each body-part group (8 groups) from the 'part' vertex attribute */
function partCentroids(g: THREE.BufferGeometry): Float32Array {
  const out = new Float32Array(8 * 4), n = new Float32Array(8);
  const pos = g.getAttribute('position'), part = g.getAttribute('part');
  if (!part) return out;
  for (let i = 0; i < pos.count; i++) { const k = part.getX(i) | 0; out[k * 4] += pos.getX(i); out[k * 4 + 1] += pos.getY(i); out[k * 4 + 2] += pos.getZ(i); n[k]++; }
  for (let k = 0; k < 8; k++) if (n[k]) { out[k * 4] /= n[k]; out[k * 4 + 1] /= n[k]; out[k * 4 + 2] /= n[k]; }
  for (let i = 0; i < pos.count; i++) {
    const k = part.getX(i) | 0, d = Math.hypot(pos.getX(i) - out[k * 4], pos.getY(i) - out[k * 4 + 1], pos.getZ(i) - out[k * 4 + 2]);
    if (d > out[k * 4 + 3]) out[k * 4 + 3] = d;
  }
  for (let k = 0; k < 8; k++) if (n[k] < 9) out[k * 4 + 3] = 0; // too small to be a piece of its own
  return out;
}

/** Pack all per-vertex (non-instanced, non-interleaved) float attributes of g into one interleaved buffer (one GPU vertex
 *  buffer instead of one each). Instanced attributes are left alone. Values are copied unchanged. */
export function interleaveVertexAttributes(g: THREE.BufferGeometry): void {
  const names = Object.keys(g.attributes).filter((k) => {
    const a = g.attributes[k] as THREE.BufferAttribute;
    return !(a as unknown as { isInstancedBufferAttribute?: boolean }).isInstancedBufferAttribute && !(a as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute && a.array instanceof Float32Array;
  });
  if (names.length < 2) return;
  const n = g.attributes.position.count;
  let stride = 0; for (const k of names) stride += g.attributes[k].itemSize;
  const data = new Float32Array(n * stride);
  let off = 0;
  const views: [string, number, number, boolean][] = [];
  for (const k of names) {
    const a = g.attributes[k] as THREE.BufferAttribute, sz = a.itemSize, src = a.array as Float32Array;
    for (let i = 0; i < n; i++) for (let c = 0; c < sz; c++) data[i * stride + off + c] = src[i * sz + c];
    views.push([k, sz, off, a.normalized]); off += sz;
  }
  const ib = new THREE.InterleavedBuffer(data, stride);
  for (const [k, sz, o, norm] of views) g.setAttribute(k, new THREE.InterleavedBufferAttribute(ib, sz, o, norm));
}
